import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { ArrowLeft, Plus, Trash2, CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/numberFormat';
import { VolumeDistribution, ContractType } from '@/types';
import { toast } from 'sonner';
import { useCreateContract } from '@/hooks/useContracts';
import { useBusinessUnitSearch } from '@/hooks/useBusinessUnits';
import { useSupplierSearch } from '@/hooks/useSuppliers';
import { useProductSearch } from '@/hooks/useProducts';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

export default function NewContract() {
  const navigate = useNavigate();
  const createContract = useCreateContract();
  
  // Search state for supplier, product, and business unit comboboxes
  const [supplierSearch, setSupplierSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [businessUnitSearch, setBusinessUnitSearch] = useState('');
  const debouncedSupplierSearch = useDebouncedValue(supplierSearch, 300);
  const debouncedProductSearch = useDebouncedValue(productSearch, 300);
  const debouncedBusinessUnitSearch = useDebouncedValue(businessUnitSearch, 300);
  
  // Supplier, product, and business unit search queries
  const { data: suppliers = [], isLoading: suppliersLoading } = useSupplierSearch(debouncedSupplierSearch);
  const { data: products = [], isLoading: productsLoading } = useProductSearch(debouncedProductSearch);
  const { data: businessUnits = [], isLoading: businessUnitsLoading } = useBusinessUnitSearch(debouncedBusinessUnitSearch);

  // Contract header fields
  const [contractName, setContractName] = useState('');
  const [contractDate, setContractDate] = useState<Date>();
  const [supplierName, setSupplierName] = useState('');
  const [productName, setProductName] = useState('');
  const [contractType, setContractType] = useState<ContractType>('Volume');
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [volumeKgs, setVolumeKgs] = useState('');
  const [businessUnitMode, setBusinessUnitMode] = useState<'Single' | 'Several'>('Single');
  
  // Fields for single BU mode
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [incoTerm, setIncoTerm] = useState('');
  const [incoTermPlace, setIncoTermPlace] = useState('');
  const [pricePerKg, setPricePerKg] = useState('');
  const [currency, setCurrency] = useState('EUR');
  
  // Common fields
  const [shippingInstructions, setShippingInstructions] = useState('');
  
  // Volume distributions for 'Several' mode
  const [volumeDistributions, setVolumeDistributions] = useState<VolumeDistribution[]>([]);

  const addVolumeDistribution = () => {
    const newId = Date.now(); // Use timestamp as temporary unique ID
    setVolumeDistributions([
      ...volumeDistributions,
      {
        id: newId,
        businessUnitId: 0,
        businessUnitName: '',
        productName: productName,
        supplierName: supplierName,
        volumeKgs: 0,
        incoTerm: '',
        incoTermPlace: '',
        pricePerKg: 0,
        currency: 'EUR',
        shippingInstructions: '',
      },
    ]);
  };

  const removeVolumeDistribution = (id: number) => {
    setVolumeDistributions(volumeDistributions.filter((vd) => vd.id !== id));
  };

  const updateVolumeDistribution = (id: number, field: keyof VolumeDistribution, value: unknown) => {
    setVolumeDistributions(
      volumeDistributions.map((vd) =>
        vd.id === id ? { ...vd, [field]: value } : vd
      )
    );
  };

  const totalDistributedVolume = volumeDistributions.reduce((sum, vd) => sum + vd.volumeKgs, 0);
  const remainingVolume = parseFloat(volumeKgs || '0') - totalDistributedVolume;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!contractDate || !startDate || !endDate) {
      toast.error('Please select all dates');
      return;
    }
    if (!contractName.trim()) {
      toast.error('Please enter a contract name');
      return;
    }
    if (!supplierName.trim()) {
      toast.error('Please select a supplier');
      return;
    }
    if (!productName.trim()) {
      toast.error('Please select a product');
      return;
    }
    if (!volumeKgs || parseFloat(volumeKgs) <= 0) {
      toast.error('Please enter a valid volume');
      return;
    }

    if (businessUnitMode === 'Single') {
      if (!businessUnitId) {
        toast.error('Please select a business unit');
        return;
      }
      if (!incoTerm || !incoTermPlace) {
        toast.error('Please complete Inco Term details');
        return;
      }
      if (!pricePerKg || parseFloat(pricePerKg) <= 0) {
        toast.error('Please enter a valid price per kg');
        return;
      }
    } else {
      if (volumeDistributions.length === 0) {
        toast.error('Please add at least one volume distribution');
        return;
      }
      if (Math.abs(remainingVolume) > 0.01) {
        toast.error(`Volume distribution must equal total volume. Remaining: ${remainingVolume.toFixed(0)} kgs`);
        return;
      }
      const hasInvalidDistribution = volumeDistributions.some(
        (vd) => !vd.businessUnitId || vd.volumeKgs <= 0 || !vd.incoTerm || vd.pricePerKg <= 0
      );
      if (hasInvalidDistribution) {
        toast.error('Please complete all volume distribution details');
        return;
      }
    }

    // Build request object
    const request = {
      contractName: contractName.trim(),
      contractDate: contractDate.toISOString(),
      supplierName: supplierName.trim(),
      productName: productName.trim(),
      contractType,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      volumeKgs: parseFloat(volumeKgs),
      businessUnitMode,
      businessUnitId: businessUnitMode === 'Single' ? parseInt(businessUnitId) : undefined,
      incoTerm: businessUnitMode === 'Single' ? incoTerm : undefined,
      incoTermPlace: businessUnitMode === 'Single' ? incoTermPlace : undefined,
      pricePerKg: businessUnitMode === 'Single' ? parseFloat(pricePerKg) : undefined,
      currency: businessUnitMode === 'Single' ? currency : undefined,
      shippingInstructions: shippingInstructions || undefined,
      volumeDistributions: businessUnitMode === 'Several' ? volumeDistributions.map(vd => ({
        businessUnitId: vd.businessUnitId,
        productName: vd.productName || productName,
        supplierName: vd.supplierName || supplierName,
        volumeKgs: vd.volumeKgs,
        incoTerm: vd.incoTerm,
        incoTermPlace: vd.incoTermPlace,
        pricePerKg: vd.pricePerKg,
        currency: vd.currency,
        shippingInstructions: vd.shippingInstructions || undefined,
      })) : undefined,
    };

    try {
      await createContract.mutateAsync(request);
      toast.success('Contract created successfully!');
      navigate('/contracts');
    } catch (error: unknown) {
      // Error toast already shown by hook
      console.error('Failed to create contract:', error);
    }
  };

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Link to="/contracts">
            <Button type="button" variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Contracts
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">New Contract</h1>
          <p className="text-muted-foreground mt-1">Create a new contract with header details and volume distribution</p>
        </div>

        {/* Contract Header Information */}
        <Card>
          <CardHeader>
            <CardTitle>Contract Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contractName">Contract Name *</Label>
                <Input
                  id="contractName"
                  placeholder="Enter contract name"
                  value={contractName}
                  onChange={(e) => setContractName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Contract Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !contractDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {contractDate ? format(contractDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={contractDate}
                      onSelect={setContractDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier *</Label>
                <Combobox
                  options={suppliers.map(s => ({
                    value: s.name,
                    label: s.name,
                    sublabel: s.code + (s.country ? ` - ${s.country}` : ''),
                  }))}
                  value={supplierName}
                  onValueChange={setSupplierName}
                  onSearchChange={setSupplierSearch}
                  placeholder="Search and select supplier..."
                  searchPlaceholder="Type to search suppliers..."
                  emptyMessage="No suppliers found. Try a different search."
                  isLoading={suppliersLoading}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="product">Product *</Label>
                <Combobox
                  options={products.map(p => ({
                    value: p.name,
                    label: p.name,
                    sublabel: p.code + (p.category ? ` - ${p.category}` : ''),
                  }))}
                  value={productName}
                  onValueChange={setProductName}
                  onSearchChange={setProductSearch}
                  placeholder="Search and select product..."
                  searchPlaceholder="Type to search products..."
                  emptyMessage="No products found. Try a different search."
                  isLoading={productsLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contractType">Contract Type *</Label>
                <Select value={contractType} onValueChange={(v) => setContractType(v as ContractType)}>
                  <SelectTrigger id="contractType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Volume">Volume Contract</SelectItem>
                    <SelectItem value="Price">Price Contract</SelectItem>
                    <SelectItem value="BaisseClause">Baisse Clause Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !startDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !endDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={endDate} onSelect={setEndDate} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="volumeKgs">Volume (kgs) *</Label>
                <NumberInput
                  id="volumeKgs"
                  placeholder="0"
                  value={volumeKgs}
                  onValueChange={(v) => setVolumeKgs(v.toString())}
                  decimals={2}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessUnitMode">Business Unit Assignment *</Label>
              <Select value={businessUnitMode} onValueChange={(v) => setBusinessUnitMode(v as 'Single' | 'Several')}>
                <SelectTrigger id="businessUnitMode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Single">Single Business Unit</SelectItem>
                  <SelectItem value="Several">Multiple Business Units</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {businessUnitMode === 'Single' && (
              <>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="businessUnit">Business Unit *</Label>
                    <Combobox
                      value={businessUnitId}
                      onValueChange={setBusinessUnitId}
                      onSearchChange={setBusinessUnitSearch}
                      placeholder="Search business units..."
                      emptyMessage="No business units found"
                      isLoading={businessUnitsLoading}
                      options={businessUnits.map((bu) => ({
                        value: bu.id.toString(),
                        label: bu.name,
                        sublabel: `${bu.code} - ${bu.location}`,
                      }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="incoTerm">Inco Term *</Label>
                    <Input
                      id="incoTerm"
                      placeholder="e.g., FOB, CIF, EXW"
                      value={incoTerm}
                      onChange={(e) => setIncoTerm(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="incoTermPlace">Incoterm Place *</Label>
                    <Input
                      id="incoTermPlace"
                      placeholder="e.g., Rotterdam"
                      value={incoTermPlace}
                      onChange={(e) => setIncoTermPlace(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pricePerKg">Price per Kg *</Label>
                    <NumberInput
                      id="pricePerKg"
                      placeholder="0.000"
                      value={pricePerKg}
                      onValueChange={(v) => setPricePerKg(v.toString())}
                      decimals={3}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency *</Label>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger id="currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shippingInstructions">Shipping Instructions for this Business Unit</Label>
                  <Textarea
                    id="shippingInstructions"
                    placeholder="Enter shipping instructions"
                    value={shippingInstructions}
                    onChange={(e) => setShippingInstructions(e.target.value)}
                    rows={3}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Volume Distributions (only shown when businessUnitMode is 'several') */}
        {businessUnitMode === 'Several' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Contract Distribution over Business Units</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Total: {formatNumber(parseFloat(volumeKgs || '0'), 2)} kgs | 
                    Distributed: {formatNumber(totalDistributedVolume, 2)} kgs | 
                    Remaining: <span className={remainingVolume < 0 ? 'text-destructive' : 'text-muted-foreground'}>{formatNumber(remainingVolume, 2)} kgs</span>
                  </p>
                </div>
                <Button type="button" onClick={addVolumeDistribution} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add BU
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {volumeDistributions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No distributions added yet. Click "Add BU" to get started.
                </div>
              ) : (
                volumeDistributions.map((vd, index) => (
                  <div key={vd.id} className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">Business Unit {index + 1}</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVolumeDistribution(vd.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Business Unit *</Label>
                        <Combobox
                          value={vd.businessUnitId ? vd.businessUnitId.toString() : ''}
                          onValueChange={(v) => updateVolumeDistribution(vd.id, 'businessUnitId', parseInt(v))}
                          onSearchChange={setBusinessUnitSearch}
                          placeholder="Search business units..."
                          emptyMessage="No business units found"
                          isLoading={businessUnitsLoading}
                          options={businessUnits.map((bu) => ({
                            value: bu.id.toString(),
                            label: bu.name,
                            sublabel: `${bu.code} - ${bu.location}`,
                          }))}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Supplier (Local Entity)</Label>
                        <Input
                          placeholder="Local supplier/distributor"
                          value={vd.supplierName}
                          onChange={(e) => updateVolumeDistribution(vd.id, 'supplierName', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label>Volume (kgs) *</Label>
                        <NumberInput
                          placeholder="0"
                          value={vd.volumeKgs || 0}
                          onValueChange={(v) => updateVolumeDistribution(vd.id, 'volumeKgs', v)}
                          decimals={2}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Inco Term *</Label>
                        <Input
                          placeholder="e.g., FOB, CIF"
                          value={vd.incoTerm}
                          onChange={(e) => updateVolumeDistribution(vd.id, 'incoTerm', e.target.value)}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Inco Term Place *</Label>
                        <Input
                          placeholder="e.g., Rotterdam"
                          value={vd.incoTermPlace}
                          onChange={(e) => updateVolumeDistribution(vd.id, 'incoTermPlace', e.target.value)}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <Label>Price per Kg *</Label>
                        <NumberInput
                          placeholder="0.000"
                          value={vd.pricePerKg || 0}
                          onValueChange={(v) => updateVolumeDistribution(vd.id, 'pricePerKg', v)}
                          decimals={3}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Currency *</Label>
                        <Select
                          value={vd.currency}
                          onValueChange={(v) => updateVolumeDistribution(vd.id, 'currency', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Shipping Instructions for this Business Unit</Label>
                      <Textarea
                        placeholder="Enter shipping instructions"
                        value={vd.shippingInstructions}
                        onChange={(e) => updateVolumeDistribution(vd.id, 'shippingInstructions', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Submit Buttons */}
        <div className="flex justify-end gap-4">
          <Link to="/contracts">
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
          <Button type="submit" disabled={createContract.isPending}>
            {createContract.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Contract
          </Button>
        </div>
      </form>
    </Layout>
  );
}
